/**
 * One-page custom checkout.
 *
 * Everything happens on a single page:
 *  1. Email step — detects new vs known user, offers login code or password
 *  2. Billing details — saved address card (logged-in) or inline form (guest)
 *  3. Donation — optional preset chips
 *  4. Pay — mounts Stripe Payment Element inline once a PaymentIntent is created
 *
 * Basket summary (sidebar + mobile bar) is fetched from /api/basket/get
 * and re-rendered after promo-code changes.
 */
export default function setupCustomCheckout () {
  const config = document.getElementById('checkout-config')
  if (!config) return // Not on the custom checkout page

  const page = document.querySelector('.checkout-page')
  if (!page) return

  const state = {
    securityId: config.dataset.securityId || '',
    isLoggedIn: config.dataset.isLoggedIn === '1',
    hasMembership: config.dataset.hasMembership === '1',
    // True only for a membership-ONLY basket (Stripe Subscription), which must
    // attach to a logged-in account. The Join & Book combined flow (membership
    // + tickets) is guest-allowed, so this is 0 there — see the Pay guard.
    membershipForcesLogin: config.dataset.membershipForcesLogin === '1',
    hasSavedAddress: config.dataset.hasSavedAddress === '1',
    stripePublishableKey: config.dataset.stripePublishableKey || '',
    stripeReturnUrl: config.dataset.stripeReturnUrl || '/purchase/checkout-confirmation',
    holdExpiresAt: parseInt(config.dataset.holdExpiresAt || '0', 10),
    basketTotal: 0,
    currentEmail: document.getElementById('checkout-email')
      ? (document.getElementById('checkout-email').value || '').trim() : '',
    paymentMounted: false,
    stripe: null,
    stripeElements: null,
    stripePaymentElement: null
  }

  let donationFunds = []
  try { donationFunds = JSON.parse(config.dataset.donationFunds || '[]') } catch (e) {}

  // ─── Init ───────────────────────────────────────────────
  startHoldTimer(page, state.holdExpiresAt)
  setupDonationSection(donationFunds)
  setupEmailStep(page, state)
  setupMobileSummaryToggle(page)
  setupPromoCode(page, state)
  setupPayButton(page, state)
  setupDonationPresets(page, state)
  setupDetailValidation(page)

  // Fetch basket once on load
  fetchAndRenderSummary(page, state)

  // Page lock state: logged-in users are unlocked; guests start locked
  // until they click Continue on the email step. If the email field is
  // prefilled from a prior session, keep it locked — server doesn't know
  // whether the prefill is still valid, only the user's Continue click does.
  if (state.isLoggedIn) {
    page.setAttribute('data-email-state', 'logged-in')
    page.classList.remove('is-email-locked')
  } else {
    page.classList.add('is-email-locked')
  }
}

// ─── Hold countdown ────────────────────────────────────────

function startHoldTimer (page, expiresAt) {
  if (!expiresAt) return
  const timerEl = page.querySelector('[data-basket-timer]')
  const mobilePill = page.querySelector('[data-mobile-timer]')
  if (!timerEl && !mobilePill) return

  const pad = n => (n < 10 ? '0' + n : '' + n)

  function render () {
    const remaining = expiresAt - Math.floor(Date.now() / 1000)
    if (remaining <= 0) {
      if (timerEl) {
        timerEl.classList.remove('checkout-timer-bar--warning')
        timerEl.classList.add('checkout-timer-bar--expired')
        timerEl.innerHTML = 'Your tickets have been released. <a href="/purchase/basket">Start again</a>'
      }
      if (mobilePill) mobilePill.textContent = 'expired'
      clearInterval(interval)
      return
    }
    const mins = Math.floor(remaining / 60)
    const secs = remaining % 60
    const label = pad(mins) + ':' + pad(secs)
    if (timerEl) {
      timerEl.innerHTML = 'Tickets held for <strong>' + label + '</strong>'
      timerEl.classList.toggle('checkout-timer-bar--warning', remaining <= 120)
    }
    if (mobilePill) mobilePill.textContent = label
  }

  render()
  const interval = setInterval(render, 1000)
}

// ─── Basket summary (sidebar + mobile) ────────────────────

async function fetchAndRenderSummary (page, state) {
  try {
    const res = await fetch('/api/basket/get')
    if (!res.ok) return
    const data = await res.json()

    if (data.expired || isBasketEmpty(data)) {
      // Basket went away — send them back
      window.location.href = '/purchase/basket'
      return
    }

    renderSummary(page, data)
    state.basketTotal = data.total || 0
    updatePayAmount(page, state.basketTotal)
  } catch (err) {
    console.error('Checkout summary fetch error:', err)
  }
}

function isBasketEmpty (data) {
  const t = (data.grouped && data.grouped.length) || 0
  const m = (data.memberships && data.memberships.length) || 0
  const g = (data.giftVouchers && data.giftVouchers.length) || 0
  return (t + m + g) === 0
}

function renderSummary (page, data) {
  const itemsHost = page.querySelector('[data-summary-items]')
  const countEl = page.querySelector('[data-summary-count]')
  const countMobile = page.querySelector('[data-summary-count-mobile]')
  const subtotalEl = page.querySelector('[data-summary-subtotal]')
  const discountRow = page.querySelector('[data-summary-discount-row]')
  const discountLabel = page.querySelector('[data-summary-discount-label]')
  const discountEl = page.querySelector('[data-summary-discount]')
  const donationRow = page.querySelector('[data-summary-donation-row]')
  const donationEl = page.querySelector('[data-summary-donation]')
  const totalEl = page.querySelector('[data-summary-total]')
  const totalMobile = page.querySelector('[data-summary-total-mobile]')
  const mirror = page.querySelector('[data-summary-mirror]')

  // Reflect current donation in the presets (e.g. on initial load)
  syncDonationPresets(page, data)

  const count = (data.count || 0) + ((data.giftVouchers || []).length)
  const hasMixed = (data.memberships && data.memberships.length > 0) || (data.giftVouchers && data.giftVouchers.length > 0)
  const label = hasMixed ? 'item' : 'ticket'
  const countStr = count === 1 ? '1 ' + label : count + ' ' + label + 's'
  if (countEl) countEl.textContent = countStr
  if (countMobile) countMobile.textContent = countStr

  // Line items — memberships + gift vouchers + tickets
  const itemsHtml = []

  for (const m of (data.memberships || [])) {
    const offer = data.membershipOffer
    itemsHtml.push(renderLine({
      thumb: m.image || '',
      name: m.name,
      meta: 'Membership',
      // With a first-month welcome offer, the line shows what's due TODAY and
      // spells out the recurring price, so the discounted total below doesn't
      // read as the full price (and the offer isn't invisible on this page).
      type: offer ? ('first month, then ' + formatPrice(m.price) + '/month') : '',
      price: offer ? (m.price * (100 - offer.percent)) / 100 : m.price,
      wasPrice: offer ? m.price : 0
    }))
  }

  for (const g of (data.giftVouchers || [])) {
    itemsHtml.push(renderLine({
      thumb: '',
      name: 'Gift voucher',
      meta: 'To: ' + (g.toName || ''),
      type: '',
      price: g.amount
    }))
  }

  for (const line of (data.grouped || [])) {
    const lineTotalInc = (line.lineTotal || 0) + (line.lineBookingFees || 0)
    itemsHtml.push(renderLine({
      thumb: line.showImage || '',
      name: line.showName || 'Ticket',
      meta: line.showDate || '',
      type: (line.qty || 1) + ' × ' + (line.ticketType || 'Ticket'),
      price: lineTotalInc,
      isFree: !!line.lineIsFree
    }))
  }

  if (itemsHost) itemsHost.innerHTML = itemsHtml.join('')

  // Totals — subtotal EXCLUDES booking fees; the fees get their own
  // "Service & Booking fee" row (matching the basket summary).
  const feesRow = page.querySelector('[data-summary-fees-row]')
  const feesEl = page.querySelector('[data-summary-fees]')
  const subtotal = data.subtotal || 0
  const fees = data.totalBookingFees || 0
  // The membership welcome offer is a first-month Stripe-coupon discount (not a
  // basket line); it has its OWN row below, so a Join & Book basket can show
  // both it and the ticket discount (they used to share a row, which dropped
  // whichever lost).
  const memOffer = data.membershipOffer
  const discount = data.totalDiscount || 0
  const donation = data.totalDonation || 0
  const total = data.total || 0

  if (subtotalEl) subtotalEl.textContent = formatPrice(subtotal)
  if (feesRow) feesRow.style.display = fees > 0 ? '' : 'none'
  if (feesEl) feesEl.textContent = formatPrice(fees)
  if (totalEl) totalEl.textContent = formatPrice(total)
  if (totalMobile) totalMobile.textContent = formatPrice(total)

  if (discountRow) discountRow.style.display = discount > 0 ? '' : 'none'
  if (discountEl) discountEl.textContent = '-' + formatPrice(discount)
  if (discountLabel) {
    discountLabel.textContent = data.promoCode ? 'Discount (' + data.promoCode + ')' : 'Discount'
  }

  const offerRow = page.querySelector('[data-summary-offer-row]')
  const offerEl = page.querySelector('[data-summary-offer]')
  const offerLabel = page.querySelector('[data-summary-offer-label]')
  const offerDiscount = memOffer ? (memOffer.discount || 0) : 0
  if (offerRow) offerRow.style.display = offerDiscount > 0 ? '' : 'none'
  if (offerEl) offerEl.textContent = '-' + formatPrice(offerDiscount)
  if (offerLabel && memOffer) offerLabel.textContent = 'First month ' + memOffer.percent + '% off'

  if (donationRow) donationRow.style.display = donation > 0 ? '' : 'none'
  if (donationEl) donationEl.textContent = formatPrice(donation)

  // Promo state
  const promoForm = page.querySelector('[data-promo-form]')
  const promoApplied = page.querySelector('[data-promo-applied]')
  const promoCodeEl = page.querySelector('[data-promo-code]')
  if (data.promoCode) {
    if (promoForm) promoForm.style.display = 'none'
    if (promoApplied) promoApplied.classList.add('is-visible')
    if (promoCodeEl) promoCodeEl.textContent = data.promoCode
  } else {
    if (promoForm) promoForm.style.display = ''
    if (promoApplied) promoApplied.classList.remove('is-visible')
    const input = page.querySelector('[data-promo-input]')
    if (input) input.value = ''
  }

  // Mobile collapsible panel — full summary, not just the item list: the
  // desktop aside is display:none on mobile, so without the totals + promo
  // section here a phone buyer had no discount breakdown, no code entry and
  // no applied-code chip (the chip "disappeared" on mobile). Same class names
  // as the aside so the CSS applies; promo events are DELEGATED (setupPromoCode)
  // so these re-rendered copies stay live.
  if (mirror) {
    const rowsHtml = []
    const row = (label, value, mod) => '<div class="checkout-summary__row' + (mod ? ' checkout-summary__row--' + mod : '') + '"><span>' + label + '</span><span>' + value + '</span></div>'
    rowsHtml.push(row('Subtotal', formatPrice(subtotal)))
    if (discount > 0) {
      const dLabel = data.promoCode ? 'Discount (' + escapeHtml(data.promoCode) + ')' : 'Discount'
      rowsHtml.push(row(dLabel, '-' + formatPrice(discount), 'discount'))
    }
    if (offerDiscount > 0) {
      rowsHtml.push(row('First month ' + memOffer.percent + '% off', '-' + formatPrice(offerDiscount), 'discount'))
    }
    if (fees > 0) rowsHtml.push(row('Service &amp; Booking fee', formatPrice(fees)))
    if (donation > 0) rowsHtml.push(row('Donation', formatPrice(donation)))
    rowsHtml.push(row('Total', formatPrice(total), 'total'))

    const promoHtml = data.promoCode
      ? '<div class="checkout-summary__promo">' +
          '<div class="checkout-summary__promo-applied is-visible">' +
            '<div class="checkout-summary__promo-tag">' +
              '<span>' + escapeHtml(data.promoCode) + '</span>' +
              '<button type="button" class="checkout-summary__promo-remove" data-promo-remove title="Remove code">&times;</button>' +
            '</div>' +
          '</div>' +
        '</div>'
      : '<div class="checkout-summary__promo">' +
          '<label class="checkout-summary__promo-label">Discount code</label>' +
          '<div class="checkout-summary__promo-row" data-promo-form>' +
            '<input type="text" class="checkout-summary__promo-input" data-promo-input placeholder="Enter code" autocomplete="off" spellcheck="false">' +
            '<button type="button" class="checkout-summary__promo-btn" data-promo-apply>Apply</button>' +
          '</div>' +
          '<p class="checkout-summary__promo-error" data-promo-error role="alert" hidden></p>' +
        '</div>'

    mirror.innerHTML =
      '<div class="checkout-summary__items">' + itemsHtml.join('') + '</div>' +
      '<div class="checkout-summary__totals">' + rowsHtml.join('') + '</div>' +
      promoHtml
  }
}

function renderLine (l) {
  const thumb = l.thumb
    ? '<div class="checkout-summary__thumb" style="background-image:url(' + escapeAttr(l.thumb) + ');background-size:cover;"></div>'
    : '<div class="checkout-summary__thumb"></div>'
  const type = l.type ? '<div class="checkout-summary__item-type">' + escapeHtml(l.type) + '</div>' : ''
  const meta = l.meta ? '<div class="checkout-summary__item-meta">' + escapeHtml(l.meta) + '</div>' : ''
  return '' +
    '<div class="checkout-summary__item">' +
      thumb +
      '<div>' +
        '<div class="checkout-summary__item-name">' + escapeHtml(l.name) + '</div>' +
        meta +
        type +
      '</div>' +
      '<div class="checkout-summary__item-price">' +
        (l.wasPrice > l.price ? '<s class="checkout-summary__item-was">' + formatPrice(l.wasPrice) + '</s> ' : '') +
        (l.isFree ? 'Free' : formatPrice(l.price)) +
      '</div>' +
    '</div>'
}

function formatPrice (n) {
  return '£' + (Number(n) || 0).toFixed(2)
}

function updatePayAmount (page, total) {
  page.querySelectorAll('[data-pay-amount]').forEach(el => {
    el.textContent = formatPrice(total)
  })
}

// ─── Email step + login flow ──────────────────────────────

function setupEmailStep (page, state) {
  const emailInput = document.getElementById('checkout-email')
  if (!emailInput || state.isLoggedIn) return

  const continueBtn = page.querySelector('[data-email-continue]')
  const editBtns = page.querySelectorAll('[data-email-edit]')
  const errEl = page.querySelector('[data-field="email"] [data-field-error]')
  const field = page.querySelector('[data-field="email"]')

  let lastCheckedEmail = ''

  const showError = (msg) => {
    if (errEl) errEl.textContent = msg
    if (field) field.classList.add('is-error')
  }
  const clearError = () => {
    if (field) field.classList.remove('is-error')
  }

  const runCheck = async () => {
    const email = (emailInput.value || '').trim()
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('Please enter a valid email address.')
      emailInput.focus()
      return
    }
    clearError()

    lastCheckedEmail = email
    state.currentEmail = email

    if (continueBtn) {
      continueBtn.disabled = true
      continueBtn.dataset.originalText = continueBtn.dataset.originalText || continueBtn.innerHTML
      continueBtn.textContent = 'Checking…'
    }

    try {
      const body = 'email=' + encodeURIComponent(email)
                 + '&SecurityID=' + encodeURIComponent(state.securityId)
      const res = await fetch('/purchase/checkout-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body
      })
      const data = await res.json()

      // Make the email read-only after Continue; the "Change" link reopens it.
      emailInput.readOnly = true

      // The ONLY branch is the basket-derived membership gate — we learn
      // nothing about whether the email is a known account (no existence
      // oracle). Tickets (guestAllowed): unlock the form; guest pay is the
      // default, with an optional "log in with a code" affordance above it.
      // Membership (!guestAllowed): keep the form locked (Pay hidden) and show
      // the log-in / create-account gate — the buyer must authenticate first.
      if (data.guestAllowed === false) {
        page.classList.add('is-email-locked')
        page.setAttribute('data-email-state', 'member-gate')
      } else {
        page.classList.remove('is-email-locked')
        page.setAttribute('data-email-state', 'ready')
      }

      // If the customer they just identified themselves as has already
      // used the applied promo code, the server cleared it from their
      // session. Refresh the summary so the totals reflect the change.
      if (data.promoDropped) {
        alert('You’ve already used the discount code on your basket, so we’ve removed it.')
        fetchAndRenderSummary(page, state)
      }
    } catch (err) {
      console.error('checkout-email error:', err)
      showError('Could not check that email. Please try again.')
    } finally {
      if (continueBtn) {
        continueBtn.disabled = false
        if (continueBtn.dataset.originalText) continueBtn.innerHTML = continueBtn.dataset.originalText
      }
    }
  }

  // Continue button click → run check
  if (continueBtn) {
    continueBtn.addEventListener('click', runCheck)
  }

  // "Change" / "Use a different email" (on the email field, the code step and
  // the register step) — reset to the empty email state, re-lock the page and
  // re-enable the input so they can start from a fresh address.
  editBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      page.setAttribute('data-email-state', 'empty')
      page.classList.add('is-email-locked')
      emailInput.readOnly = false
      lastCheckedEmail = ''
      state.currentEmail = ''
      setTimeout(() => emailInput.focus(), 30)
    })
  })

  // Enter key in email field acts as Continue
  emailInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      runCheck()
    }
  })
  emailInput.addEventListener('input', () => {
    // Clear any prior error on input
    clearError()
  })

  // "Log in with a code" → send a login code. Shown unconditionally (optional
  // on a tickets basket, the primary path on the membership gate), so clicking
  // it reveals nothing about whether the email is a known account.
  page.querySelectorAll('[data-send-login-code]').forEach(btn => {
    btn.addEventListener('click', () => sendLoginCode(page, state))
  })

  // Create account → complete registration. The register step is revealed only
  // after the code proves a brand-new email (see maybeSubmitCode), so there's
  // no self-select sign-up button any more.
  const registerBtn = page.querySelector('[data-register-submit]')
  if (registerBtn) registerBtn.addEventListener('click', () => submitRegister(page, state))

  // Resend the code — one unified code, always re-sent via send-login-code.
  page.querySelectorAll('[data-resend-code]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault()
      sendLoginCode(page, state, true)
    })
  })

  // 6-digit code input — auto-advance + paste handling + submit on complete
  setupCodeInputs(page, state)
}

async function sendLoginCode (page, state, isResend) {
  const email = state.currentEmail
  if (!email) return

  const btns = page.querySelectorAll('[data-send-login-code]')
  btns.forEach(b => { b.disabled = true })

  // N52: send failures surface inline. Before the code step the error shows on
  // the active state's send slot (the button doubles as the retry CTA); on a
  // resend we're already on the code step, so use that step's error slot. There
  // are two send slots (tickets + membership), only one ever visible — set all.
  const sendErrs = page.querySelectorAll('[data-send-code-error]')
  const codeErr = page.querySelector('[data-code-error]')
  sendErrs.forEach(el => { el.style.display = 'none'; el.textContent = '' })

  try {
    const res = await fetch('/account/send-login-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: 'email=' + encodeURIComponent(email)
          + '&SecurityID=' + encodeURIComponent(state.securityId)
    })
    // N52: a non-2xx (e.g. 502) means the send genuinely failed for an existing
    // account — surface it instead of silently advancing to the code step.
    if (!res.ok) {
      let msg = 'We couldn’t send your login code right now. Please try again in a moment.'
      try { const d = await res.json(); if (d && d.error) msg = d.error } catch (e) {}
      throw new Error(msg)
    }
    showCodeSent(page, state)
  } catch (err) {
    console.error('sendLoginCode error:', err)
    const message = (err && err.message) ? err.message
      : 'We couldn’t send your login code right now. Please try again in a moment.'
    if (isResend && codeErr) {
      codeErr.textContent = message
      const block = page.querySelector('[data-code-sent-block]')
      if (block) block.classList.add('is-error')
    } else if (sendErrs.length) {
      sendErrs.forEach(el => { el.textContent = message; el.style.display = '' })
    } else {
      alert(message)
    }
  } finally {
    btns.forEach(b => { b.disabled = false })
  }
}

// Advance to the 6-digit code step. The entered code is verified by
// maybeSubmitCode against the single verify-login-code endpoint, which either
// logs an existing row in or returns needsRegistration for a brand-new email.
function showCodeSent (page, state) {
  page.setAttribute('data-email-state', 'code-sent')
  const emailStrong = page.querySelector('[data-code-email]')
  if (emailStrong) emailStrong.textContent = state.currentEmail
  const inputs = page.querySelectorAll('[data-code-input] input')
  inputs.forEach(i => { i.value = '' })
  if (inputs[0]) setTimeout(() => inputs[0].focus(), 50)
  const block = page.querySelector('[data-code-sent-block]')
  if (block) block.classList.remove('is-error')
}

// "Create account & continue" (revealed after the code proves a brand-new
// email): collect the name and complete registration. complete-registration is
// gated on the verified-email marker the code set, so it only mints an account
// for an inbox the code already proved. On success we reload into the
// logged-in checkout (saved details + the correct buy path).
async function submitRegister (page, state) {
  const firstName = getVal('checkout-reg-firstname')
  const lastName = getVal('checkout-reg-lastname')
  const errEl = page.querySelector('[data-register-error]')
  const btn = page.querySelector('[data-register-submit]')
  const showErr = (msg) => { if (errEl) { errEl.textContent = msg; errEl.style.display = '' } }

  if (errEl) errEl.style.display = 'none'
  if (!state.currentEmail) return
  if (!firstName || !lastName) {
    showErr('Please enter your first and last name.')
    return
  }

  if (btn) btn.disabled = true
  try {
    const fd = new FormData()
    fd.append('email', state.currentEmail)
    fd.append('firstName', firstName)
    fd.append('lastName', lastName)
    fd.append('SecurityID', state.securityId)
    const res = await fetch('/account/complete-registration', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'X-Security-Token': state.securityId },
      body: fd
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) {
      // A lapsed verified-email marker (status 'expired') sends them back to
      // re-enter the email; anything else shows inline.
      if (data.status === 'expired') {
        page.setAttribute('data-email-state', 'empty')
        page.classList.add('is-email-locked')
        const emailInput = document.getElementById('checkout-email')
        if (emailInput) { emailInput.readOnly = false; setTimeout(() => emailInput.focus(), 30) }
      }
      showErr(data.error || 'We couldn’t complete your registration. Please try again.')
      return
    }
    // Logged in — reload so the checkout re-renders with logged-in state.
    window.location.href = '/purchase/custom-checkout'
  } catch (err) {
    console.error('complete-registration error:', err)
    showErr('Something went wrong. Please try again.')
  } finally {
    if (btn) btn.disabled = false
  }
}

function setupCodeInputs (page, state) {
  const inputs = page.querySelectorAll('[data-code-input] input')
  if (!inputs.length) return

  // Spread `digits` across the boxes from startIdx. Shared by paste and input:
  // on Android the code usually arrives through `input` rather than `paste`
  // (Gboard's clipboard chip inserts it without firing a paste event), which is
  // also why the inputs carry no maxlength — it truncated the insertion to one
  // digit before this could run.
  const fill = (digits, startIdx) => {
    for (let i = startIdx; i < inputs.length; i++) {
      inputs[i].value = digits.charAt(i - startIdx) || ''
    }
    inputs[Math.min(startIdx + digits.length, inputs.length - 1)].focus()
  }

  inputs.forEach((input, i) => {
    input.addEventListener('input', () => {
      // Mail clients and Gboard hand the code over with spaces/newlines between
      // the digits, so strip first, then decide: one digit, or a whole code?
      const digits = input.value.replace(/\D/g, '')
      if (digits.length > 1) {
        // Longer than the group means a stray digit was already sitting in
        // front of the insertion — keep the trailing inputs.length digits.
        if (digits.length >= inputs.length) {
          fill(digits.slice(digits.length - inputs.length), 0)
        } else {
          fill(digits, i)
        }
      } else {
        input.value = digits
        if (digits && i < inputs.length - 1) {
          inputs[i + 1].focus()
        }
      }
      maybeSubmitCode(page, state, inputs)
    })

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && !input.value && i > 0) {
        inputs[i - 1].focus()
      }
      if (e.key === 'ArrowLeft' && i > 0) inputs[i - 1].focus()
      if (e.key === 'ArrowRight' && i < inputs.length - 1) inputs[i + 1].focus()
    })

    input.addEventListener('paste', (e) => {
      e.preventDefault()
      const text = (e.clipboardData || window.clipboardData).getData('text') || ''
      const digits = text.replace(/\D/g, '').slice(0, inputs.length)
      if (!digits) return
      fill(digits, 0)
      maybeSubmitCode(page, state, inputs)
    })
  })
}

async function maybeSubmitCode (page, state, inputs) {
  const code = Array.from(inputs).map(i => i.value).join('')
  if (code.length !== 6) return

  const block = page.querySelector('[data-code-sent-block]')
  const errEl = page.querySelector('[data-code-error]')
  const codeInputWrap = page.querySelector('[data-code-input]')

  if (block) block.classList.remove('is-error')
  if (codeInputWrap) codeInputWrap.classList.remove('is-error')

  inputs.forEach(i => { i.disabled = true })

  // One unified verify endpoint. The server logs an EXISTING row straight in
  // (reload into the logged-in checkout); a brand-new email comes back as
  // needsRegistration (inbox proven, no account yet) and we reveal the name
  // step, which finishes via complete-registration.
  try {
    const res = await fetch('/account/verify-login-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: 'email=' + encodeURIComponent(state.currentEmail)
          + '&code=' + encodeURIComponent(code)
          + '&SecurityID=' + encodeURIComponent(state.securityId)
    })
    const data = await res.json().catch(() => ({}))

    if (data.success) {
      // Logged in — reload so the server re-renders with logged-in state +
      // saved address card and the correct buy path.
      window.location.href = '/purchase/custom-checkout'
      return
    }

    if (data.needsRegistration) {
      // Inbox proven, no account yet → reveal the name step (complete-registration).
      page.setAttribute('data-email-state', 'register')
      const fn = document.getElementById('checkout-reg-firstname')
      if (fn) setTimeout(() => fn.focus(), 30)
      return
    }

    // Wrong / expired code — show message + clear inputs
    if (errEl) errEl.textContent = data.error || 'That code is incorrect.'
    if (block) block.classList.add('is-error')
    if (codeInputWrap) codeInputWrap.classList.add('is-error')
    inputs.forEach(i => { i.value = '' })
    inputs[0].focus()
  } catch (err) {
    console.error('verify-login-code error:', err)
    if (errEl) errEl.textContent = 'Something went wrong. Please try again.'
    if (block) block.classList.add('is-error')
  } finally {
    inputs.forEach(i => { i.disabled = false })
  }
}

// ─── Donation presets ─────────────────────────────────────

function setupDonationSection (funds) {
  const section = document.querySelector('[data-donation-section]')
  if (!section || !funds || !funds.length) return
  // Use the first fund as the target
  const fundInput = document.getElementById('checkout-donation-fund')
  if (fundInput) fundInput.value = funds[0].id || ''
  section.style.display = ''
}

function setupDonationPresets (page, state) {
  const presets = page.querySelectorAll('[data-donation-presets] .donation-preset')
  const customWrap = page.querySelector('[data-donation-custom]')
  const customInput = document.getElementById('checkout-donation-amount')
  const customApply = page.querySelector('[data-donation-custom-apply]')
  if (!presets.length) return

  const setActive = (btn) => {
    presets.forEach(b => b.classList.remove('is-active'))
    if (btn) btn.classList.add('is-active')
  }

  presets.forEach(btn => {
    btn.addEventListener('click', () => {
      const amount = btn.dataset.donationAmount
      if (amount === 'other') {
        setActive(btn)
        if (customWrap) customWrap.classList.add('is-open')
        if (customInput) setTimeout(() => customInput.focus(), 30)
        return
      }
      // Numeric preset (including "None" with amount=0)
      if (customWrap) customWrap.classList.remove('is-open')
      if (customInput) customInput.value = ''
      commitDonation(page, state, presets, btn, parseFloat(amount) || 0)
    })
  })

  if (customApply && customInput) {
    const errorEl = page.querySelector('[data-donation-error]')
    const setDonationError = (msg) => {
      if (!errorEl) return
      errorEl.textContent = msg
      errorEl.hidden = false
    }
    const clearDonationError = () => {
      if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true }
    }

    customApply.addEventListener('click', () => {
      clearDonationError()
      const raw = customInput.value
      const amt = parseFloat(raw)
      // Branched messages so the user knows WHY the input was rejected
      // (vs the previous one-size-fits-all "Please enter an amount" alert).
      if (raw === '' || raw === null) {
        setDonationError('Enter an amount to donate.')
        customInput.focus()
        return
      }
      if (isNaN(amt)) {
        setDonationError('Please enter a number.')
        customInput.focus()
        return
      }
      if (amt <= 0) {
        setDonationError('Donation must be at least £1.')
        customInput.focus()
        return
      }
      if (amt > 9999) {
        setDonationError('Donations of more than £9,999 aren’t supported here.')
        customInput.focus()
        return
      }
      const otherBtn = page.querySelector('[data-donation-presets] [data-donation-amount="other"]')
      commitDonation(page, state, presets, otherBtn, amt)
    })
    customInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); customApply.click() }
    })
    customInput.addEventListener('input', () => clearDonationError())
  }
}

function syncDonationPresets (page, data) {
  const presets = page.querySelectorAll('[data-donation-presets] .donation-preset')
  if (!presets.length) return
  const donation = data.totalDonation || 0
  const customInput = document.getElementById('checkout-donation-amount')
  const customWrap = page.querySelector('[data-donation-custom]')

  presets.forEach(b => b.classList.remove('is-active'))

  if (donation === 0) {
    // None
    const none = page.querySelector('[data-donation-presets] [data-donation-amount="0"]')
    if (none) none.classList.add('is-active')
    if (customWrap) customWrap.classList.remove('is-open')
    if (customInput) customInput.value = ''
    return
  }

  // Match a preset (2, 5, 10) or fall back to "Other"
  const match = page.querySelector(`[data-donation-presets] [data-donation-amount="${donation}"]`)
  if (match) {
    match.classList.add('is-active')
    if (customWrap) customWrap.classList.remove('is-open')
    if (customInput) customInput.value = ''
  } else {
    const other = page.querySelector('[data-donation-presets] [data-donation-amount="other"]')
    if (other) other.classList.add('is-active')
    if (customWrap) customWrap.classList.add('is-open')
    if (customInput) customInput.value = donation.toFixed(2)
  }
}

async function commitDonation (page, state, presets, activeBtn, amount) {
  // Lock presets while the API call is in flight
  presets.forEach(b => { b.disabled = true })
  // Optimistically activate the clicked preset
  presets.forEach(b => b.classList.remove('is-active'))
  if (activeBtn) activeBtn.classList.add('is-active')

  const fundInput = document.getElementById('checkout-donation-fund')
  const fundId = fundInput ? fundInput.value : ''

  const errorEl = page.querySelector('[data-donation-error]')
  const setDonationError = (msg) => {
    if (!errorEl) return
    errorEl.textContent = msg
    errorEl.hidden = false
  }

  try {
    const res = await fetch('/api/basket/donation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Security-Token': state.securityId
      },
      body: JSON.stringify({ fundId, amount })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      setDonationError(data.error || 'Could not update donation. Please try again.')
      return
    }
    // Re-fetch basket so sidebar totals + Pay button update
    await fetchAndRenderSummary(page, state)
  } catch (err) {
    console.error('commitDonation error:', err)
    setDonationError('Something went wrong. Please try again.')
  } finally {
    presets.forEach(b => { b.disabled = false })
  }
}

// ─── Mobile sticky summary toggle ─────────────────────────

function setupMobileSummaryToggle (page) {
  const wrap = page.querySelector('#checkoutMobileSummary')
  const bar = page.querySelector('[data-mobile-summary-toggle]')
  if (!wrap || !bar) return
  bar.addEventListener('click', () => wrap.classList.toggle('is-open'))
}

// ─── Promo code (summary sidebar) ─────────────────────────

function setupPromoCode (page, state) {
  // DELEGATED, not bound per-element: the mobile summary panel re-renders its
  // own copy of the promo form/chip on every summary refresh, so direct
  // listeners would go stale. One listener pair serves the desktop aside and
  // every re-rendered mobile copy.
  page.addEventListener('click', (e) => {
    if (e.target.closest('[data-promo-remove]')) {
      applyPromo(page, state, '')
      return
    }
    const applyBtn = e.target.closest('[data-promo-apply]')
    if (applyBtn) {
      const scope = applyBtn.closest('[data-promo-form]') || page
      const input = scope.querySelector('[data-promo-input]')
      applyPromo(page, state, input ? input.value.trim() : '')
    }
  })
  page.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || !e.target.matches || !e.target.matches('[data-promo-input]')) return
    e.preventDefault()
    applyPromo(page, state, e.target.value.trim())
  })
}

async function applyPromo (page, state, code) {
  // All copies: the desktop aside's form plus the mobile panel's re-rendered one.
  const applyBtns = [...page.querySelectorAll('[data-promo-apply]')]
  const errorEls = [...page.querySelectorAll('[data-promo-error]')]
  const showPromoError = (msg) => {
    errorEls.forEach(el => { el.textContent = msg; el.hidden = false })
  }
  const clearPromoError = () => {
    errorEls.forEach(el => { el.textContent = ''; el.hidden = true })
  }

  // Map raw API error strings to friendlier user-facing copy.
  // Anything we don't recognise falls back to a generic message.
  const friendlyPromoError = (raw) => {
    if (!raw) return 'We couldn’t apply that code. Please try again.'
    const lower = String(raw).toLowerCase()
    if (lower.includes('not found') || lower.includes('invalid') || lower.includes('expired')) {
      return 'We couldn’t find that code. Please check and try again.'
    }
    if (lower.includes('already used')) return 'You have already used this code.'
    return raw
  }

  clearPromoError()
  applyBtns.forEach(b => { b.disabled = true; b.textContent = 'Applying…' })
  try {
    const res = await fetch('/api/basket/promo', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Security-Token': state.securityId
      },
      body: JSON.stringify({ code })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      showPromoError(friendlyPromoError(data.error))
      return
    }
    if (data.warning) showPromoError(data.warning)
    fetchAndRenderSummary(page, state)
  } catch (err) {
    console.error('promo code error:', err)
    showPromoError('Something went wrong. Please try again.')
  } finally {
    applyBtns.forEach(b => { b.disabled = false; b.textContent = 'Apply' })
  }
}

// ─── Pay button → initiate payment ────────────────────────

// Clear a detail field's inline error the moment the buyer starts fixing it,
// so the red state doesn't linger after they've typed. The error is raised in
// the Pay handler (submitPayment) using the same .checkout-field.is-error hook.
function setupDetailValidation (page) {
  ['checkout-firstname', 'checkout-lastname', 'checkout-postcode'].forEach(id => {
    const input = document.getElementById(id)
    if (!input) return
    input.addEventListener('input', () => {
      const fieldEl = input.closest('.checkout-field')
      if (fieldEl && input.value.trim()) fieldEl.classList.remove('is-error')
    })
  })
}

function setupPayButton (page, state) {
  page.querySelectorAll('[data-pay-btn]').forEach(btn => {
    btn.addEventListener('click', () => handlePay(page, state, btn))
  })
}

async function handlePay (page, state, btn) {
  if (state.paymentMounted) return

  // Safety net: a basket with a membership must be tied to a logged-in,
  // inbox-verified account — membership-ONLY AND Join & Book combined alike
  // (#538). The server rejects an unauthenticated membership/combined purchase
  // with 401 (initiateMembershipPayment / initiateCombinedPayment); the form
  // also stays locked in the membership-gate state (guestAllowed=false) so Pay
  // isn't normally reachable here. Keys off the same membershipForcesLogin flag
  // as those server gates so it can't drift.
  if (state.membershipForcesLogin && !state.isLoggedIn) {
    const emailInput = document.getElementById('checkout-email')
    if (emailInput) emailInput.focus()
    return
  }

  // Read email for guest checkout
  if (!state.isLoggedIn) {
    const emailInput = document.getElementById('checkout-email')
    const typed = emailInput ? (emailInput.value || '').trim() : ''
    if (!typed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typed)) {
      if (emailInput) emailInput.focus()
      alert('Please enter a valid email address.')
      return
    }
    state.currentEmail = typed
  }

  const firstName = getVal('checkout-firstname')
  const lastName = getVal('checkout-lastname')
  const postcode = getVal('checkout-postcode')

  // Inline field validation (no popup) — mirrors the email field's is-error
  // pattern: mark each empty field, focus the first, and let the input handler
  // (setupDetailValidation) clear the mark as the buyer fills it in.
  let firstInvalid = null
  for (const [id, val] of [['checkout-firstname', firstName], ['checkout-lastname', lastName], ['checkout-postcode', postcode]]) {
    const input = document.getElementById(id)
    const fieldEl = input ? input.closest('.checkout-field') : null
    const invalid = !val
    if (fieldEl) fieldEl.classList.toggle('is-error', invalid)
    if (invalid && !firstInvalid) firstInvalid = input
  }
  if (firstInvalid) {
    firstInvalid.focus()
    return
  }

  const email = state.isLoggedIn ? getVal('checkout-email') : state.currentEmail

  // Disable all pay buttons
  const payBtns = page.querySelectorAll('[data-pay-btn]')
  payBtns.forEach(b => { b.disabled = true })

  // Body
  let body = 'firstName=' + encodeURIComponent(firstName)
           + '&lastName=' + encodeURIComponent(lastName)
           + '&email=' + encodeURIComponent(email)
           + '&postcode=' + encodeURIComponent(postcode)
           + '&SecurityID=' + encodeURIComponent(state.securityId)

  // PECR refuse-at-collection: always send the explicit state so the
  // server-side session flag mirrors the latest submission (a re-submit
  // with the box unticked clears an earlier tick).
  const optOutBox = document.getElementById('checkout-marketing-optout')
  body += '&marketingOptOut=' + (optOutBox && optOutBox.checked ? '1' : '0')

  // Donation
  const fundId = getVal('checkout-donation-fund')
  const donationAmount = parseFloat(getVal('checkout-donation-amount') || '0')
  if (fundId && donationAmount > 0) {
    body += '&donationFundId=' + encodeURIComponent(fundId)
    body += '&donationAmount=' + encodeURIComponent(donationAmount.toFixed(2))
  }

  try {
    const res = await fetch('/purchase/initiate-payment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body
    })
    const data = await res.json()
    if (data.free && data.redirectUrl) {
      // Fully-discounted basket (£0) — the order was created server-side,
      // there's nothing to pay. Go straight to the confirmation page.
      window.location.href = data.redirectUrl
      return
    }
    // Join & Book combined checkout: the server returns a SetupIntent (one
    // card capture) instead of a PaymentIntent. Confirm it, then finalize the
    // membership + ticket charges server-side.
    if (data.mode === 'combined' && data.setupClientSecret) {
      mountStripeElementsCombined(page, state, data, email, payBtns)
      return
    }
    if (data.error || !data.clientSecret) {
      alert(data.error || 'Could not start payment. Please try again.')
      payBtns.forEach(b => { b.disabled = false })
      return
    }

    mountStripeElements(page, state, data, email, payBtns)
  } catch (err) {
    console.error('initiate-payment error:', err)
    alert('Something went wrong. Please try again.')
    payBtns.forEach(b => { b.disabled = false })
  }
}

function mountStripeElements (page, state, data, email, payBtns) {
  const slot = page.querySelector('[data-payment-slot]')
  const container = page.querySelector('[data-payment-container]')
  const errDiv = page.querySelector('[data-payment-error]')
  const confirmBtn = page.querySelector('[data-pay-confirm-btn]')
  if (!slot || !container || !confirmBtn) return

  // Stripe.js needs to have loaded (script tag in CustomCheckoutPage.ss).
  const publishableKey = data.publishableKey || state.stripePublishableKey
  if (typeof window.Stripe !== 'function' || !publishableKey) {
    if (errDiv) {
      errDiv.textContent = 'Payments unavailable: Stripe failed to load.'
      errDiv.classList.add('is-visible')
    }
    payBtns.forEach(b => { b.disabled = false })
    return
  }

  state.paymentMounted = true
  slot.classList.add('is-active')
  container.innerHTML = ''
  if (errDiv) { errDiv.textContent = ''; errDiv.classList.remove('is-visible') }

  if (!state.stripe) {
    state.stripe = window.Stripe(publishableKey)
  }
  state.stripeElements = state.stripe.elements({
    clientSecret: data.clientSecret,
    appearance: { theme: 'stripe' }
  })
  // We collect name + postcode in our own form, so tell the Payment
  // Element to skip its built-in billing fields. We provide the same
  // details to stripe.confirmPayment via payment_method_data below.
  state.stripePaymentElement = state.stripeElements.create('payment', {
    layout: 'tabs',
    fields: { billingDetails: 'never' }
  })
  state.stripePaymentElement.mount(container)

  // Hide the original Pay buttons (they triggered the initiate-payment call).
  // Reveal the inline Confirm button so the user submits card details.
  payBtns.forEach(b => { b.style.display = 'none' })
  confirmBtn.style.display = ''
  confirmBtn.disabled = false
  // #397: show the amount the server will actually charge — the real first
  // invoice (discounted when the welcome coupon applied, full price when it
  // didn't) — not the basket's displayed total, so the confirm button can't
  // promise a discount Stripe won't honour.
  const confirmTotal = (typeof data.amount === 'number') ? (data.amount / 100) : state.basketTotal
  updatePayAmount(page, confirmTotal)
  // #397: if the welcome offer couldn't be applied, explain the standard price
  // rather than leaving the buyer to notice the jump from the basket figure.
  const noticeEl = page.querySelector('[data-payment-notice]')
  if (noticeEl) {
    if (data.notice) {
      noticeEl.textContent = data.notice
      noticeEl.style.display = ''
    } else {
      noticeEl.textContent = ''
      noticeEl.style.display = 'none'
    }
  }

  setTimeout(() => slot.scrollIntoView({ behavior: 'smooth', block: 'center' }), 30)

  const returnUrl = data.returnUrl || state.stripeReturnUrl

  confirmBtn.addEventListener('click', async () => {
    confirmBtn.disabled = true
    if (errDiv) { errDiv.textContent = ''; errDiv.classList.remove('is-visible') }

    // Forward our minimal billing details (name + email + postcode) on the
    // PaymentMethod so Stripe can run AVS on the postcode and use the
    // name/email for receipts and risk scoring.
    const firstName = getVal('checkout-firstname')
    const lastName = getVal('checkout-lastname')
    const postcode = getVal('checkout-postcode')
    const fullName = (firstName + ' ' + lastName).trim()

    // Stripe requires every field the Payment Element opted out of
    // (fields.billingDetails='never') to be present here — including
    // phone, which we deliberately don't collect. Pass empty strings
    // for everything we don't have so confirmPayment doesn't throw an
    // IntegrationError.
    const params = {
      elements: state.stripeElements,
      confirmParams: {
        return_url: returnUrl,
        receipt_email: email || undefined,
        payment_method_data: {
          billing_details: {
            name: fullName || '',
            email: email || '',
            phone: '',
            address: {
              line1: '',
              line2: '',
              city: '',
              state: '',
              postal_code: postcode || '',
              country: 'GB'
            }
          }
        }
      }
    }

    const result = await state.stripe.confirmPayment(params)

    // confirmPayment only returns here on terminal client-side errors
    // (validation / immediate decline). 3DS + redirect flows leave the
    // page entirely before this resolves.
    if (result.error) {
      if (errDiv) {
        errDiv.textContent = result.error.message || 'Payment was declined. Please try a different card.'
        errDiv.classList.add('is-visible')
      }
      confirmBtn.disabled = false
      // N27: a decline leaves the Payment Element mounted and the Confirm
      // button live, so the customer just retries inline. But state.paymentMounted
      // stayed true — so if they instead reloaded and clicked the ORIGINAL Pay
      // button, handlePay() short-circuited (`if (state.paymentMounted) return`)
      // to a dead button. Reset it so the Pay path stays usable after a decline.
      state.paymentMounted = false
    }
  }, { once: false })
}

// Join & Book combined checkout (plan §4/§8). One card entry confirms a
// SetupIntent; the server then drives the membership + ticket charges
// off-session via /purchase/finalize-combined. The single 3DS moment (if any)
// happens during the SetupIntent confirm; a rare second step-up on the ticket
// leg is handled inline via handleNextAction.
function mountStripeElementsCombined (page, state, data, email, payBtns) {
  const slot = page.querySelector('[data-payment-slot]')
  const container = page.querySelector('[data-payment-container]')
  const errDiv = page.querySelector('[data-payment-error]')
  const confirmBtn = page.querySelector('[data-pay-confirm-btn]')
  if (!slot || !container || !confirmBtn) return

  const publishableKey = data.publishableKey || state.stripePublishableKey
  if (typeof window.Stripe !== 'function' || !publishableKey) {
    if (errDiv) {
      errDiv.textContent = 'Payments unavailable: Stripe failed to load.'
      errDiv.classList.add('is-visible')
    }
    payBtns.forEach(b => { b.disabled = false })
    return
  }

  state.paymentMounted = true
  slot.classList.add('is-active')
  container.innerHTML = ''
  if (errDiv) { errDiv.textContent = ''; errDiv.classList.remove('is-visible') }

  if (!state.stripe) {
    state.stripe = window.Stripe(publishableKey)
  }
  state.stripeElements = state.stripe.elements({
    clientSecret: data.setupClientSecret,
    appearance: { theme: 'stripe' }
  })
  state.stripePaymentElement = state.stripeElements.create('payment', {
    layout: 'tabs',
    fields: { billingDetails: 'never' }
  })
  state.stripePaymentElement.mount(container)

  payBtns.forEach(b => { b.style.display = 'none' })
  confirmBtn.style.display = ''
  confirmBtn.disabled = false
  updatePayAmount(page, state.basketTotal)

  setTimeout(() => slot.scrollIntoView({ behavior: 'smooth', block: 'center' }), 30)

  const showErr = (msg) => {
    if (errDiv) {
      errDiv.textContent = msg
      errDiv.classList.add('is-visible')
    }
  }

  // The SetupIntent is confirmed at most once; a retry (price-change
  // re-confirm, transient finalize error) re-POSTs finalize directly —
  // confirmSetup on an already-succeeded SetupIntent would just error.
  let confirmedSetupIntentId = null
  // D4: the ticket-leg gross the buyer accepted (pence). Seeded from the
  // server's initiate quote and echoed on every finalize as belt-and-braces
  // against the server-side session stamp; updated when the server returns a
  // priceChanged 409 and the buyer confirms the new amount.
  let acceptedTicketGrossPence = (typeof data.quotedTicketGrossPence === 'number')
    ? data.quotedTicketGrossPence
    : null

  confirmBtn.addEventListener('click', async () => {
    confirmBtn.disabled = true
    if (errDiv) { errDiv.textContent = ''; errDiv.classList.remove('is-visible') }

    const firstName = getVal('checkout-firstname')
    const lastName = getVal('checkout-lastname')
    const postcode = getVal('checkout-postcode')
    const fullName = (firstName + ' ' + lastName).trim()

    // 1. Confirm the SetupIntent — the single card capture (+ any 3DS, inline).
    //    Skipped on a retry once it has already succeeded.
    if (!confirmedSetupIntentId) {
      let setupResult
      try {
        setupResult = await state.stripe.confirmSetup({
          elements: state.stripeElements,
          redirect: 'if_required',
          confirmParams: {
            return_url: data.returnUrl || state.stripeReturnUrl,
            payment_method_data: {
              billing_details: {
                name: fullName || '',
                email: email || '',
                phone: '',
                address: {
                  line1: '', line2: '', city: '', state: '',
                  postal_code: postcode || '', country: 'GB'
                }
              }
            }
          }
        })
      } catch (e) {
        showErr('Something went wrong setting up your card. Please try again.')
        confirmBtn.disabled = false
        state.paymentMounted = false
        return
      }
      if (setupResult.error) {
        showErr(setupResult.error.message || 'Your card could not be set up. Please try a different card.')
        confirmBtn.disabled = false
        state.paymentMounted = false
        return
      }
      confirmedSetupIntentId = (setupResult.setupIntent && setupResult.setupIntent.id) || data.setupIntentId
      if (!confirmedSetupIntentId) {
        showErr('Your card could not be set up. Please try again.')
        confirmBtn.disabled = false
        state.paymentMounted = false
        return
      }
    }
    const setupIntentId = confirmedSetupIntentId

    // 2. Finalize: membership + ticket charges, server-side + off-session.
    let fin
    try {
      const res = await fetch(data.finalizeUrl || '/purchase/finalize-combined', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: 'setupIntentId=' + encodeURIComponent(setupIntentId)
            + '&SecurityID=' + encodeURIComponent(state.securityId)
            + (acceptedTicketGrossPence !== null
                ? '&acceptedTicketGrossPence=' + encodeURIComponent(acceptedTicketGrossPence)
                : '')
      })
      fin = await res.json()
    } catch (e) {
      showErr('Your card was set up, but we couldn’t finish your purchase. Please try again.')
      confirmBtn.disabled = false
      return
    }

    // 2-pre. D4: the server re-priced the tickets HIGHER than the quote the
    // buyer accepted and refused to charge. The membership is already active
    // (the server says so in the message); show the new amount and re-enable
    // Confirm — clicking it re-finalizes, echoing the new accepted figure,
    // and only re-runs the ticket leg.
    if (fin.priceChanged && typeof fin.newTicketGrossPence === 'number') {
      acceptedTicketGrossPence = fin.newTicketGrossPence
      updatePayAmount(page, fin.newTicketGrossPence / 100)
      showErr(fin.error || 'The ticket price has changed. Please confirm the new total to continue.')
      confirmBtn.disabled = false
      return
    }

    // 2a. Ticket leg needs a (rare) second SCA step-up.
    if (fin.requiresAction && fin.ticketClientSecret) {
      const action = await state.stripe.handleNextAction({ clientSecret: fin.ticketClientSecret })
      if (action.error) {
        // Collapses to "member, no ticket" — recoverable. Membership is active.
        showErr(action.error.message || 'Your membership is active, but the ticket payment needs another try.')
        confirmBtn.disabled = false
        return
      }
      window.location.href = fin.returnUrl
      return
    }

    // 2b. Done.
    if (fin.success && fin.redirectUrl) {
      window.location.href = fin.redirectUrl
      return
    }

    // 2c. Membership succeeded but the seats lapsed — go to the confirmation
    // (membership) page; the member can re-book.
    if (fin.membershipActive && fin.redirectUrl) {
      window.location.href = fin.redirectUrl
      return
    }

    // 2d. Error. A ticket-leg failure leaves the buyer a paid-up member with
    // the seats recoverable — surface the message and let them retry (the
    // retry runs as a normal member ticket purchase, the membership now bought).
    showErr(fin.error || 'We couldn’t complete your purchase. Please try again.')
    confirmBtn.disabled = false
    if (fin.membershipActive) {
      state.paymentMounted = false
    }
  }, { once: false })
}

// ─── Helpers ──────────────────────────────────────────────

function getVal (id) {
  const el = document.getElementById(id)
  return el ? (el.value || '').trim() : ''
}

function escapeHtml (str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttr (str) {
  return escapeHtml(str).replace(/"/g, '%22').replace(/ /g, '%20')
}
